import {
    Alert,
    Button,
    ColorIndicator,
    ConfirmModal,
    DataTable,
    Icon,
    IconButton,
    Loading,
    Spacer,
    StyleFunction,
    Surface,
    useAuth,
    useModal,
    usePagination,
    useSearch,
    useTheme,
    useThemedStyle,
} from "@venuepos/react-common";

import { ApprovalTokenType, formatDateTime, SearchDefinition } from "lib";
import { AvailableLocale, useTranslation } from "locales";
import React, { useCallback, useEffect } from "react";
import { RootStackParamList } from "../../../navigation";
import { useAdminSession } from "../../../session";
import { AdminContainer } from "../../container";
import { View } from "react-native";
import { RouteProp, useNavigation, useRoute } from "@react-navigation/native";
import { useHandleMutationError, useSearchDefinition } from "../../../hooks";
import {
    GQApprovalTokensQuery,
    useApprovalTokenDeleteMutation,
    useApprovalTokensQuery,
} from "graphql-sdk";

export function ApprovalTokenTab() {
    const auth = useAuth();
    auth.enforce(["merchant.approval"]);

    const styles = useThemedStyle(styleFunc);
    const [{ locale }] = useAdminSession(["locale"]);
    const theme = useTheme();
    const [t] = useTranslation();
    const { render } = useModal();
    const { navigate } = useNavigation();
    const route = useRoute<RouteProp<RootStackParamList, "APPROVAL_TOKENS">>();

    const [approvalTokenDelete] = useApprovalTokenDeleteMutation();
    const { handleMutationError } = useHandleMutationError();

    const { createSearchDefinition } = useSearchDefinition();
    const defaultApprovalTokenSearch: SearchDefinition = {
        name: createSearchDefinition("name"),
        createdAt: createSearchDefinition("createdAt"),
    };

    const {
        component: searchComponent,
        indicator: searchIndicator,
        search,
    } = useSearch(defaultApprovalTokenSearch, {
        visible: true,
        onSubmit: () => {
            // When the search form is submitted, then return the pagination to the first page.
            onPageChange(0);
        },
    });

    const {
        page,
        pageSize,
        sortBy,
        sortDirection,
        onSortChange,
        onPageChange,
        onPageSizeChange,
    } = usePagination({
        initialSortBy: "name",
        initialSortDirection: "ASC",
    });

    const { data, loading, error, refetch } = useApprovalTokensQuery({
        variables: {
            pagination: {
                page,
                pageSize,
                sort: sortBy,
                sortDirection: sortDirection,
            },
            searching: search,
        },
        fetchPolicy: "no-cache",
    });

    useEffect(() => {
        if (route.params?.refetch) {
            refetch();
        }
    }, [refetch, route.params?.refetch]);

    const handleEdit = useCallback(
        (id: string) => {
            navigate("APPROVAL_TOKEN_EDIT", {
                id,
            });
        },
        [navigate]
    );

    const handleDelete = useCallback(
        async (id: string) => {
            if (
                !(await render(onClose => (
                    <ConfirmModal
                        headerText={t(
                            "backoffice.approval_token.delete",
                            "Delete approval token?"
                        )}
                        bodyText={t(
                            "backoffice.approval_token.delete_explain",
                            "Are you sure, you want to delete the approval token?"
                        )}
                        onClose={onClose}
                    />
                )))
            ) {
                return;
            }

            await handleMutationError(
                async () =>
                    await approvalTokenDelete({
                        variables: {
                            id: id,
                        },
                    }),
                t(
                    "backoffice.approval_token.deleted",
                    "Approval token deleted"
                ),
                async () => {
                    await refetch();
                }
            );
        },
        [approvalTokenDelete, handleMutationError, refetch, render, t]
    );

    const translateType = useCallback(
        (type: ApprovalTokenType) => {
            switch (type) {
                case "CARD":
                    return t("backoffice.approval_token.types.CARD", "Card");
                case "CODE":
                    return t("backoffice.approval_token.types.CODE", "Code");
            }
        },
        [t]
    );

    const renderCmpEventRow = useCallback(
        (
            item: GQApprovalTokensQuery["approvalTokens"]["data"][0],
            rowIndex
        ) => (
            <DataTable.Row
                style={rowIndex % 2 ? theme.styles.oddRow : undefined}
                onPress={() => handleEdit(item.id)}
                key={`approvalId_${item.id}`}
            >
                <DataTable.Cell style={styles.iconColumn}>
                    <ColorIndicator
                        status={item.active ? "success" : "warning"}
                    />
                </DataTable.Cell>
                <DataTable.Cell>{item.name}</DataTable.Cell>
                <DataTable.Cell>{translateType(item.type)}</DataTable.Cell>
                <DataTable.Cell>{item.approvalTokenGroup?.name}</DataTable.Cell>
                <DataTable.Cell>
                    {formatDateTime(item.createdAt, locale as AvailableLocale)}
                </DataTable.Cell>
                <DataTable.Cell>
                    {formatDateTime(item.updatedAt, locale as AvailableLocale)}
                </DataTable.Cell>
                <DataTable.Cell style={styles.iconColumn} numeric>
                    <IconButton
                        color={theme.colors.secondary}
                        name="edit"
                        onPress={() => handleEdit(item.id)}
                        testID={"edit:" + item.name}
                    />
                    <IconButton
                        color={theme.colors.secondary}
                        name="delete"
                        onPress={() => handleDelete(item.id)}
                        testID={"delete:" + item.name}
                    />
                </DataTable.Cell>
            </DataTable.Row>
        ),
        [
            theme.styles.oddRow,
            theme.colors.secondary,
            styles.iconColumn,
            translateType,
            locale,
            handleEdit,
            handleDelete,
        ]
    );

    if (error) {
        return (
            <Alert type="error">
                {t(
                    "backoffice.error.from_server",
                    "There was an error: {{errorText}}",
                    {
                        errorText: error?.message,
                    }
                )}
            </Alert>
        );
    }

    return (
        <AdminContainer>
            <View style={[theme.styles.row, styles.container]}>
                <Button
                    onPress={() => {
                        navigate("APPROVAL_TOKEN_CREATE");
                    }}
                    style={styles.button}
                >
                    {t("backoffice.common.create", "Create")}
                </Button>
                <Spacer space={1} />
                {searchIndicator}
            </View>
            <Spacer space={2} />
            <Surface>
                {searchComponent}
                <DataTable>
                    <DataTable.Header>
                        <DataTable.Title
                            sortDirection={
                                sortBy === "active" ? sortDirection : undefined
                            }
                            onPress={() => onSortChange("active")}
                            style={styles.iconColumn}
                        >
                            {t("backoffice.common.active", "Active")}
                        </DataTable.Title>
                        <DataTable.Title
                            sortDirection={
                                sortBy === "name" ? sortDirection : undefined
                            }
                            onPress={() => onSortChange("name")}
                        >
                            {t("backoffice.approval_token.name", "Name")}
                        </DataTable.Title>
                        <DataTable.Title
                            sortDirection={
                                sortBy === "type" ? sortDirection : undefined
                            }
                            onPress={() => onSortChange("type")}
                        >
                            {t("backoffice.approval_token.type", "Type")}
                        </DataTable.Title>
                        <DataTable.Title
                            sortDirection={
                                sortBy === "approvalTokenGroup"
                                    ? sortDirection
                                    : undefined
                            }
                            onPress={() => onSortChange("approvalTokenGroup")}
                        >
                            {t(
                                "backoffice.approval_token.approval_token_group_name",
                                "Approval token group"
                            )}
                        </DataTable.Title>
                        <DataTable.Title
                            sortDirection={
                                sortBy === "createdAt"
                                    ? sortDirection
                                    : undefined
                            }
                            onPress={() => onSortChange("createdAt")}
                        >
                            {t("common.created_at", "Created at")}
                        </DataTable.Title>
                        <DataTable.Title
                            sortDirection={
                                sortBy === "updatedAt"
                                    ? sortDirection
                                    : undefined
                            }
                            onPress={() => onSortChange("updatedAt")}
                        >
                            {t("common.updated_at", "Updated at")}
                        </DataTable.Title>
                        <DataTable.Title style={styles.iconColumn} numeric>
                            <Icon name="sort" color={theme.colors.black} />
                        </DataTable.Title>
                    </DataTable.Header>
                    {loading ? (
                        <Loading />
                    ) : data.approvalTokens.data.length === 0 ? (
                        <DataTable.Row style={theme.styles.oddRow}>
                            <DataTable.Cell>
                                {t(
                                    "backoffice.approval_token.no_data",
                                    "No approval tokens."
                                )}
                            </DataTable.Cell>
                        </DataTable.Row>
                    ) : (
                        data?.approvalTokens.data.map(renderCmpEventRow)
                    )}
                    <DataTable.Pagination
                        onPageChange={onPageChange}
                        pageSize={pageSize}
                        onSizeChange={onPageSizeChange}
                        page={page}
                        numberOfPages={data?.approvalTokens.pagination.pages}
                        itemCount={data?.approvalTokens.pagination.resultCount}
                    />
                </DataTable>
            </Surface>
        </AdminContainer>
    );
}

const styleFunc: StyleFunction = () => ({
    container: {
        justifyContent: "flex-end",
        flexWrap: "wrap",
    },
    iconColumn: { flexGrow: 0, flexShrink: 0, flexBasis: 60 },
});
